import React, { Fragment, useRef } from 'react';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels, useRequestData } from 'wsm-common-data';
import AspectRatioBox from '../components/aspect-ratio-box/AspectRatioBox';
import DetailsButton from '../components/DetailsButton';
import CtaButton from '../components/CtaButton';
import { aR, g } from '../global-constants';
import { templatePropTypes } from '../utilities/commonPropTypes';
import {
	getBackgroundImage,
	getIncentiveImage,
	getNoImageLabels,
	getVehicleImages
} from '../utilities/general';
import { getCouponStatus } from '../utilities/aspect';
import { IMAGE_BREAKPOINTS } from '../utilities/images';
import OptimizedImageBackground from '../components/OptimizedImageBackground';
import { useURLPrefs } from '../hooks/useURLPrefs';

export const offerDetailsFragment = ({
	couponCode,
	cta,
	description,
	details,
	discountDisplay,
	labels,
	title
}) => (
	<Fragment>
		<h2>{title}</h2>
		{discountDisplay && (
			<div className="font-weight-bold text-capitalize discount">
				{discountDisplay}
			</div>
		)}
		<p>{description}</p>
		<div className="cta-wrapper">
			{cta}
			{couponCode ? (
				<div className="d-flex align-items-center details">
					<div className="flex-fill text-nowrap">{details}</div>
					<div className="coupon-code flex-fill justify-self-right text-right font-weight-normal">
						{`${labels.get('COUPON_CODE')}: ${couponCode}`}
					</div>
				</div>
			) : (
				details
			)}
		</div>
	</Fragment>
);

export const offerDetails10x1Fragment = ({
	couponCode,
	cta,
	details,
	labels
}) => (
	<Fragment>
		<div className="cta-wrapper">
			{cta}
			{couponCode ? (
				<div className="d-flex flex-column">
					<div className="text-nowrap">{details}</div>
					<div className="coupon-code justify-self-right text-right font-weight-normal">
						{`${labels.get('COUPON_CODE')}: ${couponCode}`}
					</div>
				</div>
			) : (
				details
			)}
		</div>
	</Fragment>
);

export const detailsButtonFragment = ({
	carouselPosition,
	detailsButtonClasses = [],
	detailsButtonData = {},
	incentive = {},
	applicableVehicle = '',
	isVisible = true,
	promo = {},
	aspectRatio = null,
	cta = {}
}) => {
	const { id, type } = detailsButtonData;

	return id && type ? (
		<DetailsButton
			btnStyle={aspectRatio === aR.MOBILE_6X1 ? 'primary' : 'link'}
			carouselPosition={carouselPosition}
			classes={detailsButtonClasses}
			incentive={incentive}
			applicableVehicle={applicableVehicle}
			isVisible={isVisible}
			listingConfigId=""
			promo={promo}
			cta={cta}
		/>
	) : null;
};

export const ctaButtonFragment = ({
	carouselPosition,
	ctaButtonClasses = [],
	ctaModalButtonClasses = [],
	ctaButtonData = {},
	isVisible = true,
	promo = {}
}) => {
	const { ctaLabel, ctaUrl } = ctaButtonData;
	const classes = ctaModalButtonClasses.length
		? ctaModalButtonClasses
		: ctaButtonClasses;

	return ctaUrl && ctaLabel ? (
		<CtaButton
			carouselPosition={carouselPosition}
			ctaButtonClasses={classes}
			isVisible={isVisible}
			label={ctaLabel}
			url={ctaUrl}
			promo={promo}
		/>
	) : null;
};

export const Mobile6x1Fragment = ({
	carouselPosition,
	detailsButtonData,
	isVisible,
	promo,
	title,
	incentive = {},
	isSlant,
	ctaButtonData
}) => {
	const ctaModalButtonClasses = setClassNames(['my-4']);

	const template = (
		<div
			className={setClassNames([
				'wrapper',
				'px-4',
				'py-3',
				isSlant && 'blue-background'
			])}
		>
			<span className="ddc-icon ddc-icon-tag d-block" />
			{title ? <h2 className="title">{title}</h2> : null}
			<div className="cta-wrapper">
				{detailsButtonFragment({
					carouselPosition,
					detailsButtonData,
					isVisible,
					promo,
					incentive,
					aspectRatio: aR.MOBILE_6X1,
					cta: ctaButtonFragment({
						carouselPosition,
						ctaModalButtonClasses,
						ctaButtonData,
						isVisible,
						promo
					})
				})}
			</div>
		</div>
	);

	return (
		<AspectRatioBox aspectRatio={{ width: 6, height: 1 }}>
			{template}
		</AspectRatioBox>
	);
};

Mobile6x1Fragment.propTypes = templatePropTypes;

// This fragment is shared between Desktop4x3 and Mobile4x3-slideshow/Mobile4x3-coupon
export const Shared4x3Fragment = ({
	applicableVehicle,
	carouselPosition,
	couponCode = '',
	ctaButtonData,
	description,
	detailsButtonData,
	discountDisplay = '',
	imageUrl,
	isVisible,
	offerDetails,
	promo,
	incentive = {},
	title,
	type,
	vehicleImages = [],
	width
}) => {
	let template;
	const labels = useLabels();
	const ctaButtonClasses = ['btn-inverse', 'd-table'];
	const detailsButtonClasses = ['p-0', 'text-decoration-underline'];
	const noImageLabels = getNoImageLabels(labels, promo.inventoryCount);
	const ctaModalButtonClasses = setClassNames(['my-4']);
	const { aspectRatio, enableCarousel, collection } = useURLPrefs();
	const { deviceType } = useRequestData();
	const isMobile = deviceType === 'MOBILE';
	const couponRef = useRef();
	const isSlant = collection.toLowerCase() === 'slant';
	const isCoupon = getCouponStatus(aspectRatio);
	const isCouponCaro = enableCarousel === 'true' && !isMobile;

	const hasDetailsButton =
		typeof promo.additionalDetails !== 'undefined' ||
		typeof promo.disclaimer !== 'undefined' ||
		Object.keys(incentive).length !== 0;
	const sharedFragment = (
		<Fragment>
			{isSlant ? (
				<div className="stripe w-100 position-relative" />
			) : null}
			<div className="d-flex flex-column justify-content-between text">
				<div className="text-data-upper">
					<h2>{title}</h2>
					<p>{description}</p>
				</div>
				<div
					className={`text-data-lower d-flex ${
						!hasDetailsButton ? 'details-padding' : ''
					}`}
				>
					<div className="text-data-left text-left">
						<div className="cta-wrapper">
							{ctaButtonFragment({
								carouselPosition,
								ctaButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})}
							{detailsButtonFragment({
								carouselPosition,
								detailsButtonClasses,
								detailsButtonData,
								isVisible,
								promo,
								cta: ctaButtonFragment({
									carouselPosition,
									ctaModalButtonClasses,
									ctaButtonData,
									isVisible,
									promo
								})
							})}
						</div>
					</div>
					<div className="text-data-right text-right">
						{discountDisplay && (
							<div className="font-weight-bold text-capitalize discount">
								{discountDisplay}
							</div>
						)}
						{couponCode && (
							<div className="coupon-code">
								{`${labels.get('COUPON_CODE')}: ${couponCode}`}
							</div>
						)}
					</div>
				</div>
			</div>
		</Fragment>
	);

	if ([g.PARTS, g.SERVICE, g.EVENT].includes(type)) {
		template = (
			<Fragment>
				<OptimizedImageBackground
					className="image w-100"
					style={{
						backgroundImage: `url("${imageUrl}")`,
						backgroundColor: 'black'
					}}
					width={isCoupon ? IMAGE_BREAKPOINTS[1] : width}
				/>
				{sharedFragment}
			</Fragment>
		);
	} else if (type === g.VEHICLE) {
		template = (
			<Fragment>
				<OptimizedImageBackground
					className="d-flex flex-row justify-content-center flex-shrink-0 align-middle image"
					style={{
						backgroundImage: `url("${getBackgroundImage(type)}")`,
						backgroundPosition: 'bottom'
					}}
					width={isCoupon ? IMAGE_BREAKPOINTS[1] : width}
					key={`${promo.id}-container`}
				>
					{getVehicleImages(
						vehicleImages,
						width,
						isMobile,
						0,
						{
							title: noImageLabels.title,
							subtitle: noImageLabels.subtitle
						},
						'vehicle-image',
						isCoupon ? 37 : null
					)}
				</OptimizedImageBackground>
				{sharedFragment}
			</Fragment>
		);
	} else if (type === g.INCENTIVE) {
		template = (
			<Fragment>
				<OptimizedImageBackground
					className="d-flex flex-row justify-content-center flex-shrink-0 align-middle image"
					style={{
						backgroundImage: `url("${getBackgroundImage(type)}")`,
						backgroundPosition: 'bottom'
					}}
					width={isCoupon ? IMAGE_BREAKPOINTS[1] : width}
				>
					{getIncentiveImage(
						imageUrl,
						width,
						'', // leave empty unless we have a label other than YMMT
						isMobile,
						50,
						collection.toLowerCase() === 'slant' ||
							collection.toLowerCase() === 'fade'
							? {
									title: noImageLabels.title,
									subtitle: noImageLabels.subtitle,
									containerStyles: {
										zIndex: 50,
										boxShadow: 'rgb(0 0 0) 0px 0px 10px'
									}
							  }
							: {
									title: noImageLabels.title,
									subtitle: noImageLabels.subtitle
							  }
					)}
				</OptimizedImageBackground>
				{isSlant ? (
					<div className="stripe w-100 position-relative" />
				) : null}
				<div className="d-flex flex-column justify-content-between text">
					<div className="text-data-upper">
						<h2>
							{applicableVehicle}
							<span className="pt-1 font-weight-normal w-100 d-block">
								{title}
							</span>
						</h2>
						{description ? <p>{description}</p> : null}
						{offerDetails ? <p>{offerDetails}</p> : null}
					</div>
					<div
						className={`text-data-lower d-flex ${
							!hasDetailsButton ? 'details-padding' : ''
						}`}
					>
						<div className="cta-wrapper">
							{ctaButtonFragment({
								carouselPosition,
								ctaButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})}
							{detailsButtonFragment({
								carouselPosition,
								detailsButtonClasses,
								detailsButtonData,
								isVisible,
								promo,
								incentive,
								applicableVehicle,
								cta: ctaButtonFragment({
									carouselPosition,
									ctaModalButtonClasses,
									ctaButtonData,
									isVisible,
									promo
								})
							})}
						</div>
					</div>
				</div>
			</Fragment>
		);
	}

	return (
		<AspectRatioBox aspectRatio={{ width: 4, height: 3 }}>
			<div
				ref={couponRef}
				className={setClassNames([
					'd-flex',
					'promo-card',
					'flex-column',
					'h-100',
					isCouponCaro ? 'carousel p-3' : ''
				])}
			>
				{template}
			</div>
		</AspectRatioBox>
	);
};

Shared4x3Fragment.propTypes = templatePropTypes;
